import { router } from "@inertiajs/react"
import { AlertTriangle, Loader2 } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

import { formatDate } from "./status-chip"
import type { PodRow } from "./types"

/**
 * The Promised-Out Date, and every date it used to be.
 *
 * Revisions are a CHAIN, not an update. "How often does this shop move the
 * date, and by how much" IS the POD-accuracy metric on the lessor scorecard —
 * overwriting the row would erase exactly the evidence being graded. So the
 * form below always ADDS a commitment, and the history stays on the page where
 * an operator can see a shop that has slipped three times.
 */
export function PodCard({
  current,
  history,
  createPath,
  canEdit,
}: {
  current: PodRow | null
  history: PodRow[]
  createPath: string
  canEdit: boolean
}) {
  const [promisedOn, setPromisedOn] = useState("")
  const [source, setSource] = useState("")
  const [saving, setSaving] = useState(false)

  const missed =
    current != null &&
    new Date(`${current.promised_on}T00:00:00`) <
      new Date(new Date().toDateString())

  const revisions = history.filter((row) => !row.current).reverse()

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (saving || !promisedOn) return
    setSaving(true)
    router.post(
      createPath,
      { pod_commitment: { promised_on: promisedOn, source: source || null } },
      {
        preserveScroll: true,
        onSuccess: () => {
          setPromisedOn("")
          setSource("")
        },
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Promised out</CardTitle>
        {missed && (
          <Badge variant="destructive">
            <AlertTriangle className="mr-1 size-3" />
            Missed
          </Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <p
            className={cn(
              "text-2xl font-semibold tabular-nums",
              missed && "text-destructive",
            )}
          >
            {current ? formatDate(current.promised_on) : "Not committed"}
          </p>
          {current?.source && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              via {current.source}
            </p>
          )}
          {!current && (
            <p className="text-muted-foreground mt-0.5 text-sm">
              No shop has committed to a date yet.
            </p>
          )}
        </div>

        {revisions.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-muted-foreground mb-2 text-xs font-medium">
              Revision history
            </p>
            <ul className="space-y-1.5">
              {revisions.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="text-muted-foreground tabular-nums line-through">
                    {formatDate(row.promised_on)}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {row.source ?? "—"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {canEdit && (
          <form className="space-y-2 border-t pt-3" onSubmit={submit}>
            <Label htmlFor="pod-promised-on" className="text-xs">
              {current ? "Revise to" : "Set date"}
            </Label>
            <div className="flex gap-1.5">
              <Input
                id="pod-promised-on"
                type="date"
                value={promisedOn}
                onChange={(event) => setPromisedOn(event.target.value)}
                className="h-9"
              />
              <Input
                value={source}
                onChange={(event) => setSource(event.target.value)}
                placeholder="Source"
                aria-label="Source"
                className="h-9 w-28"
              />
              <Button type="submit" size="sm" disabled={saving || !promisedOn}>
                {saving ? <Loader2 className="size-4 animate-spin" /> : "Add"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}
